import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Package, Users, ArrowRight, Search, Calendar, CreditCard, CheckCircle } from 'lucide-react';
import PublicNavbar from '../../components/public/navbar/PublicNavbar';
import PublicFooter from '../../components/public/footer/PublicFooter';
import SEO from '../../components/shared/SEO';
import './Home.css';

const heroImages = [
  '/images/hero/hero1.jpg', // Chicago skyline
  '/images/hero/hero2.jpg', // Film production
  '/images/hero/hero3.jpg', // Camera/filming
  '/images/hero/hero4.jpg', // Studio
  '/images/hero/hero5.jpg', // Cinema
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: MapPin,
      title: 'Premium Locations',
      description: 'Exclusive filming locations across the city - studios, rooftops, industrial spaces, and more.',
      link: '/locations',
    },
    {
      icon: Package,
      title: 'Professional Equipment',
      description: 'State-of-the-art cameras, lighting rigs, audio gear, and grip equipment.',
      link: '/equipment',
    },
    {
      icon: Users,
      title: 'Top Talent',
      description: 'Experienced directors, cinematographers, and crew for every production need.',
      link: '/talent',
    },
  ];

  const steps = [
    {
      icon: Search,
      title: 'Browse',
      description: 'Explore our curated selection of locations, equipment, and talent.',
    },
    {
      icon: Calendar,
      title: 'Select Dates',
      description: 'Pick your dates and see real-time availability and pricing.',
    },
    {
      icon: CreditCard,
      title: 'Book & Pay',
      description: 'Secure checkout with instant confirmation sent to your email.',
    },
    {
      icon: CheckCircle,
      title: 'Create',
      description: 'Show up and bring your vision to life with premium resources.',
    },
  ];

  return (
    <div className="public-home">
      <SEO
        title="Chicago Film Production Services"
        description="Premium film production services in Chicago. Rent exclusive filming locations, professional camera equipment, and hire experienced talent for your next production."
        keywords="Chicago film production, filming locations, camera equipment rental, film crew, video production"
        url="/"
        schema={{
          '@type': 'WebSite',
          'name': 'IZZISHOWIN',
          'url': 'https://izzishowin.com',
          'potentialAction': {
            '@type': 'SearchAction',
            'target': 'https://izzishowin.com/locations?search={search_term_string}',
            'query-input': 'required name=search_term_string'
          }
        }}
      />
      <PublicNavbar />

      {/* Hero Section - Dramatic & Cinematic */}
      <section className="home-hero">
        <div className="hero-slideshow">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            />
          ))}
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-vignette"></div>
        <div className="hero-content">
          <span className="hero-eyebrow">Production Services</span>
          <h1 className="hero-title">
            Elevate Your <span className="text-gold">Vision</span>
          </h1>
          <p className="hero-subtitle">
            Premium locations, professional equipment, and top talent for film,
            photo, and commercial productions.
          </p>
          <div className="hero-actions">
            <Link to="/locations" className="hero-btn hero-btn-primary">
              Start Booking
              <ArrowRight size={18} />
            </Link>
            <Link to="/equipment" className="hero-btn hero-btn-secondary">
              Browse Services
            </Link>
          </div>
        </div>
        <div className="hero-indicators">
          {heroImages.map((_, index) => (
            <button
              key={index}
              className={`hero-indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features">
        <div className="container">
          <div className="features-header">
            <p className="section-eyebrow">Our Services</p>
            <h2 className="section-title">What We Offer</h2>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Link
                  key={index}
                  to={feature.link}
                  className="feature-card"
                >
                  <div className="feature-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                  <span className="feature-link">
                    Explore <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="home-how-it-works">
        <div className="container">
          <div className="how-it-works-header">
            <p className="section-eyebrow">Simple Process</p>
            <h2 className="section-title">How It Works</h2>
          </div>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <div key={index} className="step-card">
                <div className="step-number">{index + 1}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Create Something Amazing?</h2>
            <p className="cta-description">
              Join hundreds of productions that trust IZZISHOWIN for their creative needs.
            </p>
            <Link to="/locations" className="cta-btn">
              Get Started
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default Home;
